import { Text, Spoiler, Stack, Badge, Group, Code } from "@mantine/core";
import JsonBlock from "../_Common/JsonBlock";

// Where the endpoint's origin came from. "callback" means the caller told us on this request;
// anything else means it came off the stored tool definition. Worth showing, because a run served
// by a tunnel and one served by the deployed app are otherwise indistinguishable here.
const originSourceLabels = {
  tool: { label: "tool definition", color: "gray" },
  environment: { label: "environment binding", color: "gray" },
  callback: { label: "caller-supplied origin", color: "blue" },
};

/**
 * Standalone panel for rendering a single tool call as an accordion step.
 * Shows arguments and result with visible previews (Spoiler with reasonable maxHeight).
 */
export function ToolCallStepPanel({ step }) {
  const { toolCall, startedEvent, completedEvent, functionResult } = step;
  // step.hasError is the resolved verdict (backend event first, legacy payload sniffing as a
  // fallback); recomputing it here from the event alone would disagree with the header.
  const isFailed =
    step.hasError ||
    completedEvent?.data?.status === "failed" ||
    completedEvent?.type?.toLowerCase() === "toolfailed";
  const resultPreview = completedEvent?.data?.resultPreview;

  const targetUrl =
    startedEvent?.data?.targetUrl || completedEvent?.data?.targetUrl;
  const originSource = (
    startedEvent?.data?.originSource ||
    completedEvent?.data?.originSource ||
    ""
  ).toLowerCase();
  const origin = originSourceLabels[originSource];

  const argsJson = toolCall.arguments
    ? JSON.stringify(toolCall.arguments, null, 2)
    : null;

  const resultContent = functionResult?.toolCallResult?.content;
  const resultDisplay = resultContent
    ? typeof resultContent === "string"
      ? resultContent
      : JSON.stringify(resultContent, null, 2)
    : resultPreview;

  return (
    <Stack gap="xs" p="sm">
      {targetUrl && (
        <div>
          <Text size="xs" fw={600} c="dimmed" mb={4}>
            Endpoint
          </Text>
          <Group gap="xs" wrap="wrap">
            <Code style={{ wordBreak: "break-all" }}>{targetUrl}</Code>
            {origin && (
              <Badge size="xs" variant="light" color={origin.color}>
                {origin.label}
              </Badge>
            )}
          </Group>
        </div>
      )}
      {argsJson && (
        <div>
          <Text size="xs" fw={600} c="dimmed" mb={4}>
            Arguments
          </Text>
          <Spoiler
            maxHeight={150}
            showLabel="Show more"
            hideLabel="Show less"
          >
            <JsonBlock value={argsJson} />
          </Spoiler>
        </div>
      )}
      {isFailed && (
        <Group gap="xs" wrap="wrap">
          <Badge size="xs" color="red" variant="filled">
            Call failed
          </Badge>
          {step.httpErrorCode && (
            <Badge size="xs" color="red" variant="light">
              {typeof step.httpErrorCode === "number"
                ? `HTTP ${step.httpErrorCode}`
                : step.httpErrorCode}
            </Badge>
          )}
          {step.errorKind && (
            <Badge size="xs" color="red" variant="outline">
              {step.errorKind}
            </Badge>
          )}
          <Text size="xs" c="dimmed">
            The model received this error as the tool result and continued.
          </Text>
        </Group>
      )}
      {resultDisplay ? (
        <div>
          <Text size="xs" fw={600} c="dimmed" mb={4}>
            Result
            {!functionResult?.toolCallResult?.content && resultPreview && (
              <Text component="span" c="dimmed" fs="italic">
                {" "}
                — first 500 chars, from the event log
              </Text>
            )}
          </Text>
          <Spoiler
            maxHeight={200}
            showLabel="Show more"
            hideLabel="Show less"
          >
            <Text
              size="xs"
              ff="monospace"
              c={isFailed ? "red" : undefined}
              style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >
              {resultDisplay}
            </Text>
          </Spoiler>
        </div>
      ) : (
        <Text size="xs" c="dimmed" fs="italic">
          No result data
        </Text>
      )}
    </Stack>
  );
}
